import { notFound, useRouter, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { fetchAllProducts, fetchProducts, type ShopifyProduct } from "@/lib/shopify";
import { getColorSlugsFromProduct } from "@/lib/shopify-colors";
import { ProductCard } from "@/components/ProductCard";
import { FilterBar } from "@/components/FilterBar";
import { LocaleLink } from "@/components/LocaleLink";
import { useT } from "@/lib/i18n";
import { COLOR_CATEGORIES, getColorBySlug, type ColorCategory } from "@/lib/colors";
import { SITE_URL, breadcrumbJsonLd } from "@/lib/seo";
import { FaqBlock } from "@/components/seo/FaqBlock";
import { trackProductListView } from "@/lib/analytics";
import { useScrollDepth } from "@/hooks/useScrollDepth";
import { ColorBannerSilhouettes } from "@/components/color/ColorBannerSilhouettes";
import { ColorMushroomMark } from "@/components/color/ColorMushroomMark";

export const colorLoader = async ({ params }: { params: { slug: string } }) => {
  const color = getColorBySlug(params.slug);
  if (!color) throw notFound();

  // Primary source: Shopify's `shopify.color-pattern` metafield. We scan
  // the catalog wide and keep everything the merchant has tagged with this
  // color taxonomy value.
  const all = await fetchAllProducts({
    max: 1000,
    sortKey: "BEST_SELLING",
    reverse: false,
  });
  const byMetafield = all.filter((p) =>
    getColorSlugsFromProduct(p.node).includes(color.slug),
  );

  // Fallback: products without a metafield still get picked up via the
  // legacy title search, so categories don't suddenly empty out for items
  // the merchant hasn't tagged yet. Dedup on product id.
  const byTitle = await fetchProducts({
    first: 48,
    query: color.query,
    sortKey: "BEST_SELLING",
    reverse: false,
  });
  const seen = new Set(byMetafield.map((p) => p.node.id));
  const merged = [...byMetafield];
  for (const p of byTitle) {
    if (!seen.has(p.node.id)) {
      merged.push(p);
      seen.add(p.node.id);
    }
  }

  return { color, products: merged };
};

function buildHead(loaderData: any, params: any, locale: "da" | "en") {
  const c = loaderData?.color as ColorCategory | undefined;
  const slug = params?.slug ?? "";
  const url = `${SITE_URL}${locale === "en" ? "/en" : ""}/farver/${slug}`;
  const label = c ? (locale === "en" ? c.labelEn : c.labelDa) : "";
  const intro = c ? (locale === "en" ? c.introEn : c.introDa) : "";
  const title = c
    ? locale === "en"
      ? `${label} Murano & vintage — Farveladeland`
      : `${label} Murano & vintage — Farveladeland`
    : locale === "en"
      ? "Colour — Farveladeland"
      : "Farve — Farveladeland";
  const desc = intro || (locale === "en" ? "Vintage Murano glass in your favourite colour." : "Vintage Murano-glas i din yndlingsfarve.");
  const crumb = c
    ? breadcrumbJsonLd([
        { name: locale === "en" ? "Home" : "Forsiden", url: SITE_URL },
        { name: locale === "en" ? "Colours" : "Farver", url: `${SITE_URL}${locale === "en" ? "/en" : ""}/farver` },
        { name: label, url },
      ])
    : null;
  return {
    meta: [
      { title },
      { name: "description", content: desc.slice(0, 160) },
      { property: "og:title", content: title },
      { property: "og:description", content: desc.slice(0, 160) },
    ],
    scripts: crumb
      ? [{ type: "application/ld+json", children: JSON.stringify(crumb) }]
      : [],
  };
}

export const colorHeadDa = ({ loaderData, params }: any) => buildHead(loaderData, params, "da");
export const colorHeadEn = ({ loaderData, params }: any) => buildHead(loaderData, params, "en");

export function ColorErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  return (
    <div className="mx-auto max-w-2xl px-6 pt-40 pb-32 text-center">
      <p className="eyebrow">Ups</p>
      <h1 className="mt-4 font-display text-5xl">Noget gik galt</h1>
      <p className="mt-4 text-muted-foreground">{error.message}</p>
      <button
        onClick={() => {
          router.invalidate();
          reset();
        }}
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-xs font-medium uppercase tracking-[0.22em] text-background"
      >
        Prøv igen
      </button>
    </div>
  );
}

export function ColorNotFoundComponent() {
  return (
    <div className="mx-auto max-w-2xl px-6 pt-40 pb-32 text-center">
      <p className="eyebrow">404</p>
      <h1 className="mt-4 font-display text-5xl">Den farve har vi ikke</h1>
      <LocaleLink
        to="/farver"
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-xs font-medium uppercase tracking-[0.22em] text-background"
      >
        Se alle farver →
      </LocaleLink>
    </div>
  );
}

export function ColorPage() {
  const { color, products } = useLoaderData({ strict: false }) as {
    color: ColorCategory;
    products: ShopifyProduct[];
  };
  const { t, locale } = useT();
  const intro = locale === "da" ? color.introDa : color.introEn;
  const label = locale === "da" ? color.labelDa : color.labelEn;
  const others = COLOR_CATEGORIES.filter((c) => c.slug !== color.slug);
  useScrollDepth(`color:${color.slug}`);
  const [filtered, setFiltered] = useState<ShopifyProduct[]>(products);
  const onFilterChange = useCallback(
    (next: ShopifyProduct[]) => setFiltered(next),
    [],
  );

  useEffect(() => {
    trackProductListView({
      list_id: `color:${color.slug}`,
      list_name: `Farve · ${label}`,
      item_count: products.length,
    });
  }, [color.slug, label, products.length]);

  return (
    <>
      <section className={`relative overflow-hidden pt-40 pb-16 md:pt-48 md:pb-24 ${color.panel}`}>
        <ColorBannerSilhouettes />
        <div className="relative mx-auto max-w-[1500px] px-6 md:px-10">
          <nav className="mb-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-foreground/55">
            <LocaleLink to="/" className="hover:text-foreground">
              {t("color.crumbHome")}
            </LocaleLink>
            <span>/</span>
            <LocaleLink to="/farver" className="hover:text-foreground">
              {t("color.crumbColors")}
            </LocaleLink>
            <span>/</span>
            <span className="text-foreground">{label}</span>
          </nav>

          <div className="grid items-end gap-10 md:grid-cols-[auto_1fr]">
            <ColorMushroomMark
              color={color.swatch}
              className="h-28 w-24 text-foreground md:h-40 md:w-36"
            />
            <div>
              <p className="eyebrow">{t("colors.eyebrow")}</p>
              <h1 className="mt-3 font-display text-[clamp(3rem,8vw,7rem)] leading-[0.92]">
                {label}
              </h1>
              <p className="mt-6 max-w-xl text-pretty text-base leading-relaxed text-foreground/75">
                {intro}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 pt-16 pb-24 md:px-10 md:pt-20 md:pb-32">
        {products.length === 0 ? (
          <div className="flex flex-col items-center gap-6 py-20 text-center">
            <p className="max-w-md text-muted-foreground">{t("color.empty")}</p>
            <LocaleLink
              to="/collections/$handle"
              params={{ handle: "murano-lamper" }}
              className="inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-xs font-medium uppercase tracking-[0.22em] text-background hover:bg-terracotta"
            >
              {t("color.seeAll")} →
            </LocaleLink>
          </div>
        ) : (
          <>
            <div className="mb-6 flex items-end justify-between">
              <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">
                {t("color.count", { n: products.length })}
              </p>
              <LocaleLink
                to="/farver"
                className="text-[11px] font-medium uppercase tracking-[0.24em] text-foreground/70 hover:text-terracotta"
              >
                {t("color.allColors")} →
              </LocaleLink>
            </div>
            <FilterBar products={products} onChange={onFilterChange} />
            {filtered.length === 0 ? (
              <div className="py-16 text-center text-sm text-muted-foreground">
                {t("filter.noMatch")}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-12 sm:grid-cols-3 lg:grid-cols-4">
                {filtered.map((p: ShopifyProduct, i) => (
                  <ProductCard
                    key={p.node.id}
                    product={p}
                    listId={`color:${color.slug}`}
                    listName={`Farve · ${label}`}
                    index={i}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <FaqBlock
        eyebrow={t("faq.eyebrow")}
        title={`${t("faq.titleA")} ${t("faq.titleB")}`}
        items={[
          { q: t("faq.q1.q"), a: t("faq.q1.a") },
          { q: t("faq.q2.q"), a: t("faq.q2.a") },
          { q: t("faq.q4.q"), a: t("faq.q4.a") },
          { q: t("faq.q9.q"), a: t("faq.q9.a") },
        ]}
      />

      <section className="mx-auto max-w-[1500px] border-t border-border px-6 py-16 md:px-10 md:py-24">
        <p className="eyebrow">{t("color.allColors")}</p>
        <div className="mt-8 grid grid-cols-3 gap-2 sm:grid-cols-4 md:gap-3 lg:grid-cols-7">
          {others.map((c) => (
            <LocaleLink
              key={c.slug}
              to="/farver/$slug"
              params={{ slug: c.slug }}
              className={`group flex aspect-[3/4] flex-col justify-between rounded-sm p-3 transition-transform duration-500 hover:-translate-y-1 ${c.panel}`}
            >
              <span className="sr-only">
                {locale === "da" ? c.labelDa : c.labelEn}
              </span>
              <div>
                <span
                  className="block h-5 w-5 rounded-full ring-1 ring-foreground/10"
                  style={{ backgroundColor: c.swatch }}
                  aria-hidden
                />
                <p className="mt-2 font-display text-base text-foreground">
                  {locale === "da" ? c.labelDa : c.labelEn}
                </p>
              </div>
            </LocaleLink>
          ))}
        </div>
      </section>
    </>
  );
}
